/**
 * ContactBadges Component - 聯繫徽章
 * Requirements: 6.1, 6.2
 * 
 * Telegram 徽章、Nodeseek 徽章、交易平台徽章
 */

import { ShoppingCartIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import type { ContactInfo } from '../../types/card';
import type { Translations } from '../../i18n';

interface ContactBadgesProps {
  contact: ContactInfo;
  t: Translations;
}

/** Telegram 圖標 */
function TelegramIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

export function ContactBadges({ contact, t }: ContactBadgesProps) {
  const { tg, xy, ns } = contact;
  const hasContact = tg || xy || ns;

  if (!hasContact) {
    return null;
  }

  return (
    <div className="p-4 border-t border-slate-200">
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">{t.contactInfo}</h3>
      
      <div className="flex flex-wrap gap-2">
        {/* Telegram 徽章 */}
        {tg && (
          <a
            href={`https://t.me/${tg.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <TelegramIcon />
            <span>{tg}</span>
          </a>
        )}

        {/* Nodeseek 徽章 */}
        {ns && (
          <a
            href={`https://www.nodeseek.com/space/${ns}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span>NS: {ns}</span>
          </a>
        )}

        {/* 交易平台徽章 */}
        {xy && (
          <a
            href={xy.startsWith('http') ? xy : `https://${xy}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <ShoppingCartIcon className="w-4 h-4" />
            <span>{t.tradingPlatform}</span>
          </a>
        )}
      </div>
    </div>
  );
}

export default ContactBadges;
